import type { Settings } from '@flow/core';

/**
 * The fonts a judge can pick (Settings → Display). All self-hosted in public/fonts (SIL OFL), loaded only when chosen.
 * The choice is applied as CSS variables on <html> (see App.tsx); ui/global.css holds the @font-face declarations.
 */
export interface FontChoice {
  label: string;
  family: string;
  /** Fixed-pitch (or nearly): also used for the clock digits when chosen for writing. */
  mono?: boolean;
  note?: string;
}

export const SYSTEM_FAMILY = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const MONO_FAMILY = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const SANS: Record<'inter' | 'plex' | 'source' | 'atkinson', FontChoice> = {
  inter: { label: 'Inter', family: `'Inter', ${SYSTEM_FAMILY}`, note: 'The neutral modern sans' },
  plex: { label: 'IBM Plex Sans', family: `'IBM Plex Sans', ${SYSTEM_FAMILY}`, note: 'A little more character' },
  source: { label: 'Source Sans 3', family: `'Source Sans 3', ${SYSTEM_FAMILY}`, note: 'Compact; fits the most' },
  atkinson: { label: 'Atkinson Hyperlegible', family: `'Atkinson Hyperlegible', ${SYSTEM_FAMILY}`, note: 'Made for legibility' },
};

export const UI_FONTS: Record<Settings['uiFont'], FontChoice> = {
  system: { label: 'System', family: SYSTEM_FAMILY, note: "Your device's own (San Francisco on Apple devices)" },
  ...SANS,
};

export const WRITING_FONTS: Record<Settings['writingFont'], FontChoice> = {
  inherit: { label: 'Same as interface', family: 'var(--font-ui)' },
  ...SANS,
  quattro: { label: 'iA Writer Quattro', family: `'iA Writer Quattro', 'IBM Plex Sans', ${MONO_FAMILY}`, mono: true, note: 'Typewriter rhythm, nearly sans density' },
  'plex-mono': { label: 'IBM Plex Mono', family: `'IBM Plex Mono', ${MONO_FAMILY}`, mono: true, note: 'A true monospace; takes more room' },
  jetbrains: { label: 'JetBrains Mono', family: `'JetBrains Mono', ${MONO_FAMILY}`, mono: true, note: 'A true monospace; takes more room' },
};

/** The CSS variables for a choice. `--font-mono` (the clock) follows a mono writing font, else the device's. */
export const fontVars = (uiFont: Settings['uiFont'], writingFont: Settings['writingFont']): Record<string, string> => {
  const w = WRITING_FONTS[writingFont];
  return { '--font-ui': UI_FONTS[uiFont].family, '--font-writing': w.family, '--font-mono': w.mono ? w.family : MONO_FAMILY };
};
